import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dotwApi, formatApiError, resolveImageUrl } from '../lib/api';
import { getCrownVicImage } from '../lib/images';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Trophy, X, Eye, ImagePlus, RefreshCw, ArrowLeft, CheckCircle, FileText, Star } from 'lucide-react';

const QA_TEMPLATE = [
  "Name, hometown, and track(s) raced",
  "How did you get started in Crown Vic racing?",
  "Why did you choose Crown Vic racing?",
  "Racing experience and biggest highlight",
  "Car nickname or setup details",
  "Toughest track and why",
  "People who helped your racing journey",
  "Fun fact fans might not know",
  "Goals for the season",
  "Shoutouts to sponsors, crew, or supporters",
];

export default function AdminDriverOfTheWeekPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [filterWeek, setFilterWeek] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    driver_name: '', hometown: '', car_number: '', track: '',
    week_start: new Date().toISOString().slice(0, 10),
    status: 'draft',
    qa: QA_TEMPLATE.map(q => ({ question: q, answer: '' })),
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchDrivers();
  }, [user, navigate]);

  const fetchDrivers = async () => {
    try {
      const res = await dotwApi.getAll(0, 100, filterStatus || undefined, filterWeek || undefined);
      setDrivers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!loading) { fetchDrivers(); } }, [filterWeek, filterStatus]);

  const resetForm = () => {
    setForm({
      driver_name: '', hometown: '', car_number: '', track: '',
      week_start: new Date().toISOString().slice(0, 10), status: 'draft',
      qa: QA_TEMPLATE.map(q => ({ question: q, answer: '' })),
    });
    setEditing(null);
    setError('');
  };

  const openCreate = () => { resetForm(); setIsOpen(true); };

  const openEdit = (driver) => {
    setEditing(driver);
    setForm({
      driver_name: driver.driver_name || '', hometown: driver.hometown || '',
      car_number: driver.car_number || '', track: driver.track || '',
      week_start: driver.week_start || '', status: driver.status || 'draft',
      qa: driver.qa?.length ? driver.qa : QA_TEMPLATE.map(q => ({ question: q, answer: '' })),
    });
    setIsOpen(true); setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driver_name) { setError('Driver name is required'); return; }
    setSubmitting(true); setError('');
    try {
      if (editing) {
        await dotwApi.update(editing.id, form);
      } else {
        await dotwApi.create(form);
      }
      setIsOpen(false); resetForm(); fetchDrivers();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this driver entry?')) return;
    try { await dotwApi.delete(id); setDrivers(drivers.filter(d => d.id !== id)); } catch (err) { alert(formatApiError(err)); }
  };

  const handleToggleStatus = async (driver) => {
    const newStatus = driver.status === 'published' ? 'draft' : 'published';
    try {
      await dotwApi.update(driver.id, { status: newStatus });
      setDrivers(drivers.map(d => d.id === driver.id ? { ...d, status: newStatus } : d));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleImageUpload = async (e, driverId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFor(driverId);
    try { await dotwApi.uploadImage(driverId, file); fetchDrivers(); }
    catch (err) { alert(formatApiError(err)); }
    finally { setUploadingFor(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleSetMainImage = async (driverId, index) => {
    try { await dotwApi.update(driverId, { main_image: index }); fetchDrivers(); }
    catch (err) { alert(formatApiError(err)); }
  };

  const handleRemoveImage = async (driverId, imgIndex) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return;
    const newImages = driver.images.filter((_, i) => i !== imgIndex);
    const newMain = (driver.main_image || 0) >= newImages.length ? 0 : driver.main_image;
    try { await dotwApi.update(driverId, { images: newImages, main_image: newMain }); fetchDrivers(); }
    catch (err) { alert(formatApiError(err)); }
  };

  const updateQA = (index, answer) => {
    const newQA = [...form.qa];
    newQA[index] = { ...newQA[index], answer };
    setForm({ ...form, qa: newQA });
  };

  // Group drivers by week for display
  const groupedByWeek = {};
  drivers.forEach(d => {
    const w = d.week_start || 'Unassigned';
    if (!groupedByWeek[w]) groupedByWeek[w] = [];
    groupedByWeek[w].push(d);
  });
  const sortedWeeks = Object.keys(groupedByWeek).sort((a, b) => b.localeCompare(a));
  const uniqueWeeks = [...new Set(drivers.map(d => d.week_start))].filter(Boolean).sort((a, b) => b.localeCompare(a));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/driver-of-the-week" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-2">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back to Public Page
              </Link>
              <p className="tag text-[#FF3B30] mb-2">Admin</p>
              <h1 className="font-heading font-bold text-3xl text-white">Manage Drivers of the Week</h1>
              <p className="text-gray-400 text-sm mt-1">{drivers.length} entries</p>
            </div>
            <Button onClick={openCreate} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="new-dotw-btn">
              <Plus className="w-4 h-4 mr-2" /> New Driver
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#1F1F1F] border border-[#262626] rounded px-3 py-1.5 text-sm text-white">
            <option value="">All Status</option>
            <option value="submitted">User Submissions</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <select value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)} className="bg-[#1F1F1F] border border-[#262626] rounded px-3 py-1.5 text-sm text-white">
            <option value="">All Weeks</option>
            {uniqueWeeks.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <input type="file" ref={fileInputRef} onChange={(e) => { if (uploadingFor) handleImageUpload(e, uploadingFor); }} accept="image/*" className="hidden" />

        {/* Grouped by Week */}
        {sortedWeeks.map(week => (
          <div key={week} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="font-heading font-bold text-lg text-white">Week of {week}</h3>
              <span className="text-gray-500 text-sm">({groupedByWeek[week].length} driver{groupedByWeek[week].length !== 1 ? 's' : ''})</span>
              {groupedByWeek[week].filter(d => d.status === 'published').length > 3 && (
                <span className="tag bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded text-xs">More than 3 published</span>
              )}
            </div>

            <div className="space-y-3">
              {groupedByWeek[week].map((driver) => (
                <Card key={driver.id} className="bg-[#141414] border-[#262626] p-4" data-testid={`dotw-entry-${driver.id}`}>
                  <div className="flex gap-4">
                    {/* Images with main selector */}
                    <div className="flex-shrink-0">
                      <div className="flex gap-2">
                        {(driver.images || []).slice(0, 3).map((img, i) => (
                          <div key={i} className="relative group w-20 h-20">
                            <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover rounded border border-[#262626]" />
                            {(driver.main_image || 0) === i && (
                              <div className="absolute top-0.5 left-0.5"><Star className="w-3.5 h-3.5 text-[#FF3B30] fill-[#FF3B30]" /></div>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                              <button onClick={() => handleSetMainImage(driver.id, i)} className="w-6 h-6 bg-[#FF3B30] rounded-full flex items-center justify-center" title="Set as main"><Star className="w-3 h-3 text-white" /></button>
                              <button onClick={() => handleRemoveImage(driver.id, i)} className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center" title="Remove"><X className="w-3 h-3 text-white" /></button>
                            </div>
                          </div>
                        ))}
                        {(driver.images || []).length < 3 && (
                          <button onClick={() => { setUploadingFor(driver.id); fileInputRef.current?.click(); }}
                            className="w-20 h-20 rounded border-2 border-dashed border-[#262626] hover:border-[#FF3B30] flex items-center justify-center text-gray-500 hover:text-[#FF3B30] transition-colors">
                            {uploadingFor === driver.id ? <RefreshCw className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h4 className="font-heading font-bold text-lg text-white">{driver.driver_name}</h4>
                        {driver.car_number && <span className="text-gray-500 text-sm">#{driver.car_number}</span>}
                        {(() => {
                          const s = driver.status;
                          const style =
                            s === 'published' ? 'bg-green-500/20 text-green-400'
                            : s === 'submitted' ? 'bg-[#FF3B30]/20 text-[#FF3B30]'
                            : 'bg-gray-500/20 text-gray-400';
                          const label = s === 'published' ? 'PUBLISHED' : s === 'submitted' ? 'USER SUBMISSION' : 'DRAFT';
                          return <span className={`tag px-2 py-0.5 rounded text-xs ${style}`}>{label}</span>;
                        })()}
                      </div>
                      <p className="text-gray-400 text-sm">{driver.hometown}{driver.track ? ` • ${driver.track}` : ''}</p>
                      {driver.status === 'submitted' && driver.submitted_by_email && (
                        <p className="text-[#FF3B30] text-xs mt-1">
                          Submitted by {driver.submitted_by_name || driver.submitted_by_email} · {driver.submitted_at ? new Date(driver.submitted_at).toLocaleDateString() : ''}
                        </p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        {(driver.qa || []).filter(q => q.answer).length}/{(driver.qa || []).length} Q&A answered • {(driver.images || []).length}/3 photos
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(driver)}
                        className={driver.status === 'published' ? 'text-green-400' : 'text-gray-400 hover:text-green-400'}
                        title={driver.status === 'published' ? 'Unpublish' : 'Publish'} data-testid={`toggle-status-${driver.id}`}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Link to={`/driver-of-the-week/${driver.id}`}>
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white"><Eye className="w-4 h-4" /></Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(driver)} className="text-gray-400 hover:text-white" data-testid={`edit-dotw-${driver.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(driver.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-dotw-${driver.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {drivers.length === 0 && (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="font-heading font-bold text-xl text-white mb-2">No Drivers Yet</h3>
            <Button onClick={openCreate} className="bg-[#FF3B30] hover:bg-[#E6352B] mt-4"><Plus className="w-4 h-4 mr-2" /> New Driver</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={(v) => { setIsOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? 'Edit Driver' : 'New Driver'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-3 py-2 rounded text-sm">{error}</div>}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Driver Name *</Label>
                <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} placeholder="John Smith" className="bg-[#1F1F1F] border-[#262626] text-white" required data-testid="dotw-name-input" />
              </div>
              <div>
                <Label>Hometown</Label>
                <Input value={form.hometown} onChange={(e) => setForm({ ...form, hometown: e.target.value })} placeholder="Dayton, Ohio" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Car Number</Label>
                <Input value={form.car_number} onChange={(e) => setForm({ ...form, car_number: e.target.value })} placeholder="17" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div>
                <Label>Track</Label>
                <Input value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} placeholder="Showtime Speedway" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div>
                <Label>Week Starting</Label>
                <Input type="date" value={form.week_start} onChange={(e) => setForm({ ...form, week_start: e.target.value })} className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full mt-1 p-2 bg-[#1F1F1F] border border-[#262626] rounded text-white">
                <option value="submitted">Submitted (pending review)</option>
                <option value="draft">Draft (not visible to public)</option>
                <option value="published">Published (visible on site)</option>
              </select>
            </div>

            <div className="border-t border-[#262626] pt-5">
              <h3 className="font-heading font-bold text-lg text-white mb-4">Driver Q&A</h3>
              <div className="space-y-4">
                {form.qa.map((item, i) => (
                  <div key={i}>
                    <Label className="text-[#FF3B30] text-xs uppercase tracking-wider">{item.question}</Label>
                    <Textarea value={item.answer} onChange={(e) => updateQA(i, e.target.value)} placeholder="Enter answer..." rows={2} className="bg-[#1F1F1F] border-[#262626] text-white mt-1" data-testid={`dotw-qa-${i}`} />
                  </div>
                ))}
              </div>
            </div>

            <p className="text-gray-500 text-xs">Upload photos after saving. Set to "Published" when ready for the public.</p>

            <Button type="submit" disabled={submitting} className="w-full bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="save-dotw-btn">
              {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Create Driver Entry'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
